import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/cn';

// Selo de estado (WEB-133): pílula NEUTRA, e quem diz o estado é o quadrado pequeno de cor
// (`dot`), nunca o fundo. Assim uma coluna inteira de selos numa tabela não vira uma faixa
// colorida, e o desenho é o mesmo no claro e no escuro. `paid` é o dinheiro que entrou: o
// quadrado dele é o ouro da marca.
const badgeVariants = cva(
  'inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border border-transparent px-2.5 text-xs font-medium whitespace-nowrap transition-colors duration-150 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground [--dot:var(--muted-foreground)] [a&]:hover:bg-accent',
        destructive: 'bg-secondary text-foreground [--dot:var(--destructive)] [a&]:hover:bg-accent',
        success: 'bg-secondary text-foreground [--dot:var(--success)] [a&]:hover:bg-accent',
        // Estado "aguardando" é neutro (Caio, 17/09/2026): o quadrado vazado marca a espera.
        warning:
          'bg-secondary text-foreground [--dot:transparent] [--dot-ring:inset_0_0_0_1.5px_var(--muted-foreground)] [a&]:hover:bg-accent',
        paid: 'bg-secondary text-foreground [--dot:var(--brand)] [a&]:hover:bg-accent',
        outline: 'text-foreground shadow-(--shadow-inset) [a&]:hover:bg-secondary',
        ghost: '[a&]:hover:bg-secondary [a&]:hover:text-foreground',
        link: 'text-teal underline-offset-4 [a&]:hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  dot = false,
  children,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; dot?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {dot && !asChild && (
        <span
          aria-hidden="true"
          className="size-[7px] shrink-0 bg-(--dot,currentColor)"
          style={{ boxShadow: 'var(--dot-ring, none)' }}
        />
      )}
      {children}
    </Comp>
  );
}

export { Badge, badgeVariants };
